import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, LogOut } from "lucide-react";
import { Button, Drawer } from "@nutrayan/ui";
import { useAuth } from "@/lib/auth";
import { useTenantOverride } from "@/lib/tenant-override";
import { useClaims } from "@/lib/use-claims";
import { hasPermission, isBootstrapLevel, roleLabel, tenantLabel } from "@/lib/permissions";
import { NAV_SECTIONS } from "@/lib/nav";
import { TenantSwitcher } from "@/components/TenantSwitcher";

function NavItems({
  role,
  adminLevel,
  onNavigate,
}: {
  role: string | undefined;
  adminLevel: string | undefined;
  onNavigate?: () => void;
}) {
  const sections = NAV_SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter(
      (i) =>
        hasPermission(role, i.permission) && (!i.bootstrapOnly || isBootstrapLevel(adminLevel)),
    ),
  })).filter((s) => s.items.length > 0);

  return (
    <nav className="flex flex-col gap-5">
      {sections.map((section) => (
        <div key={section.title}>
          <p className="text-caption text-muted px-3 pb-1 font-medium uppercase tracking-wide">
            {section.title}
          </p>
          <div className="flex flex-col gap-0.5">
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `text-body-sm flex items-center gap-2.5 rounded-md px-3 py-2 ${
                    isActive ? "bg-cream text-saffron font-medium" : "text-charcoal hover:bg-cream"
                  }`
                }
              >
                <item.icon size={17} aria-hidden />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2">
      <span className="bg-saffron/15 text-saffron font-display flex h-8 w-8 items-center justify-center rounded-md">
        N
      </span>
      <div>
        <p className="font-display text-heading-sm text-charcoal leading-none">Nutrayan</p>
        <p className="text-caption text-muted">Admin</p>
      </div>
    </div>
  );
}

export function Shell() {
  const auth = useAuth();
  const { role, tenantId, adminLevel } = useClaims();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const onLogout = () => {
    auth.clear();
    useTenantOverride.getState().setTenant(null);
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen">
      {/* Desktop sidebar */}
      <aside className="border-charcoal/10 bg-sand hidden w-60 shrink-0 flex-col border-r p-4 md:flex">
        <Brand />
        <div className="mt-6">
          <NavItems role={role} adminLevel={adminLevel} />
        </div>
      </aside>

      {/* Mobile nav drawer */}
      <Drawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} title="Menu" side="left">
        <NavItems role={role} adminLevel={adminLevel} onNavigate={() => setMobileNavOpen(false)} />
      </Drawer>

      <div className="flex flex-1 flex-col">
        <header className="border-charcoal/10 bg-cream flex items-center justify-between gap-4 border-b px-4 py-3 md:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open menu"
              className="text-charcoal hover:bg-sand rounded p-2 md:hidden"
            >
              <Menu size={20} aria-hidden />
            </button>
            <div className="md:hidden">
              <Brand />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <TenantSwitcher />
            <NavLink
              to="/profile"
              aria-label="My profile"
              className="hover:bg-sand flex items-center gap-2.5 rounded-full py-1 pl-1 pr-2 md:pl-2"
            >
              <span className="bg-saffron/15 text-saffron font-display flex h-9 w-9 items-center justify-center rounded-full text-sm uppercase">
                {(auth.email ?? "A").slice(0, 1)}
              </span>
              <span className="hidden text-left sm:block">
                <span className="text-body-sm text-charcoal block leading-none">
                  {auth.email ?? "—"}
                </span>
                <span className="text-caption text-muted mt-0.5 block">
                  {roleLabel(role)} · {tenantLabel(tenantId)}
                </span>
              </span>
            </NavLink>
            <Button size="sm" variant="ghost" onClick={onLogout} aria-label="Sign out">
              <LogOut size={16} aria-hidden />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </header>
        <main className="bg-cream flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
